/**
 * @file DateField.tsx
 * @description Общее поле даты кабинета (phase19.31, зона «Склад»; замечание
 * владельца «поля Сроки не вписываются»). Заменяет нативное
 * `<input type="date">`, которое браузер рисует по-своему.
 *
 * - Поле стандартной высоты (тема: 48 до 1024, 44 с 1024, 56 у кладовщика),
 *   рамка и шрифт — как у обычного поля; ввод с клавиатуры по маске
 *   «дд.мм.гггг» (точки ставятся сами, вставка «гггг-мм-дд» тоже понимается).
 * - Значок календаря справа: календарь во всплывающем окне, на телефоне — в
 *   нижней шторке; неделя с понедельника, русская локаль.
 * - Крестик очищает значение. Несуществующая дата («31.02.2027») — ошибка
 *   под полем, значение формы не меняется.
 * - Наружу — «гггг-мм-дд» или пустая строка, как у нативного поля: логика
 *   форм (пересчёт срока годности, периоды) не меняется.
 *
 * ```tsx
 * <DateField id="exp" value={expiresAt} onChange={setExpiresAt} ariaLabel="Годен до" />
 * ```
 */

import { CalendarDays, X } from "lucide-react";
import { useEffect, useState, type Ref } from "react";

import { Calendar } from "./calendar";
import { dateToIso, isoToDate, isoToDisplay, maskDateInput, parseDateInput } from "./date-field-format";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./sheet";
import { PHONE_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

interface Props {
  /** «гггг-мм-дд» или пусто. */
  value: string;
  onChange: (iso: string) => void;
  id?: string;
  /** Подпись для скринридера, если нет `<label htmlFor>`. */
  ariaLabel?: string;
  /** Ошибка снаружи (или «внимание»: срок не указан) — красит рамку. */
  invalid?: boolean;
  /** id подсказки под полем. */
  describedBy?: string;
  disabled?: boolean;
  /** Заголовок шторки календаря на телефоне. */
  title?: string;
  inputRef?: Ref<HTMLInputElement>;
  className?: string;
  /**
   * Крестик «очистить» (по умолчанию есть). phase19.31: пара «Произведено /
   * Годен до» стоит в одну линию, и на 360 крестик отнимал у даты половину
   * поля — там стирают клавиатурой (пустое поле = пустое значение).
   */
  clearable?: boolean;
}

/** Текст ошибки несуществующей даты. */
const INVALID_TEXT = "Такой даты нет — проверьте день и месяц";

export function DateField({ value, onChange, id, ariaLabel, invalid, describedBy, disabled, title = "Выберите дату", inputRef, className, clearable = true }: Props) {
  const phone = useMediaQuery(PHONE_QUERY);
  const [text, setText] = useState(() => isoToDisplay(value));
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const errorId = id ? `${id}-error` : undefined;

  // Значение поменяли снаружи (автосрок, сброс фильтра) — показываем его.
  useEffect(() => {
    const parsed = parseDateInput(text);
    if (!("iso" in parsed) || parsed.iso !== value) {
      setText(isoToDisplay(value));
      setError(null);
    }
    // text намеренно не в зависимостях: реагируем только на внешнее значение.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function type(raw: string) {
    const masked = maskDateInput(raw);
    setText(masked);
    const parsed = parseDateInput(masked);
    if ("iso" in parsed) {
      setError(null);
      if (parsed.iso !== value) onChange(parsed.iso);
    } else if ("invalid" in parsed) {
      setError(INVALID_TEXT);
    } else {
      setError(null);
    }
  }

  function pick(date: Date | undefined) {
    setOpen(false);
    if (!date) return;
    const iso = dateToIso(date);
    setText(isoToDisplay(iso));
    setError(null);
    if (iso !== value) onChange(iso);
  }

  function clear() {
    setText("");
    setError(null);
    if (value) onChange("");
  }

  const calendar = <Calendar mode="single" selected={isoToDate(value)} defaultMonth={isoToDate(value)} onSelect={pick} />;
  const triggerCls =
    "inline-flex size-[var(--control-compact-h,3rem)] shrink-0 items-center justify-center rounded-md text-on-surface-variant hover:bg-surface-low hover:text-primary disabled:opacity-50";

  return (
    <div className={cn("flex w-full flex-col gap-1 sm:w-[11rem]", className)}>
      <div
        data-slot="date-field"
        className={cn(
          "flex min-h-[var(--control-h,3rem)] items-center gap-1 rounded-lg bg-[var(--input,var(--surface-lowest))] pl-3 pr-1",
          invalid || error
            ? "[box-shadow:inset_0_0_0_1px_var(--destructive)]"
            : "[box-shadow:inset_0_0_0_1px_var(--field-border,var(--border))] focus-within:[box-shadow:inset_0_0_0_1px_var(--field-border-focus,var(--primary)),0_0_0_2px_var(--ring)]",
          disabled && "opacity-60",
        )}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="дд.мм.гггг"
          value={text}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={invalid || !!error || undefined}
          aria-describedby={[error ? errorId : null, describedBy].filter(Boolean).join(" ") || undefined}
          onChange={(e) => type(e.target.value)}
          className="min-w-0 flex-1 bg-transparent text-[length:var(--fs-field,1rem)] tabular-nums outline-none placeholder:text-on-surface-variant disabled:cursor-not-allowed"
        />
        {clearable && text && !disabled ? (
          <button type="button" onClick={clear} aria-label="Очистить дату" title="Очистить" className={triggerCls}>
            <X className="size-4" />
          </button>
        ) : null}
        {phone ? (
          <button type="button" onClick={() => setOpen(true)} disabled={disabled} aria-label="Открыть календарь" className={triggerCls}>
            <CalendarDays className="size-4" />
          </button>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
              render={(props) => (
                <button {...props} type="button" disabled={disabled} aria-label="Открыть календарь" className={triggerCls}>
                  <CalendarDays className="size-4" />
                </button>
              )}
            />
            <PopoverContent align="end" className="p-2">
              {calendar}
            </PopoverContent>
          </Popover>
        )}
      </div>
      {error ? (
        <p id={errorId} role="alert" className="type-caption text-[var(--destructive-text)]">
          {error}
        </p>
      ) : null}
      {phone ? (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="items-center gap-2 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
            <SheetHeader className="w-full">
              <SheetTitle className="type-card-title">{title}</SheetTitle>
            </SheetHeader>
            {calendar}
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
}
